import "./styles.css";
import { track } from "./analytics";
import { calculateRevenueRisk } from "./calculator";
import { loadPublicConfig, type PublicConfig } from "./config";

const $ = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element: ${selector}`);
  return element;
};

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
let publicConfig: PublicConfig;
let calculatorStarted = false;
let calculatorCompletedTimer = 0;
let elevenLabsEmbedPromise: Promise<void> | null = null;

const ELEVENLABS_WIDGET_URL = "/elevenlabs/convai-widget-embed.js";

function ensureElevenLabsWidgetDefined(): Promise<void> {
  if (customElements.get("elevenlabs-convai")) return Promise.resolve();
  if (elevenLabsEmbedPromise) return elevenLabsEmbedPromise;

  elevenLabsEmbedPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-elevenlabs-widget]');
    const script = existing || document.createElement("script");
    const ready = () => void customElements.whenDefined("elevenlabs-convai").then(() => resolve());
    const failed = () => reject(new Error("The live-call component could not be loaded."));

    script.addEventListener("load", ready, { once: true });
    script.addEventListener("error", failed, { once: true });
    if (!existing) {
      script.src = ELEVENLABS_WIDGET_URL;
      script.async = true;
      script.type = "text/javascript";
      script.dataset.elevenlabsWidget = "true";
      document.head.append(script);
    }
  }).catch((cause) => {
    elevenLabsEmbedPromise = null;
    throw cause;
  });

  return elevenLabsEmbedPromise;
}

function readNumber(id: string): number {
  return Number(($<HTMLInputElement>(`#${id}`)).value) || 0;
}

function updateCalculator(source = "initial"): void {
  const inputs = {
    monthlyCalls: readNumber("monthly-calls"),
    missedPercentage: readNumber("missed-percentage"),
    opportunityPercentage: readNumber("opportunity-percentage"),
    averageJobValue: readNumber("job-value"),
  };
  const result = calculateRevenueRisk(inputs, publicConfig.recurringPrice);
  $("#monthly-calls-output").textContent = whole.format(inputs.monthlyCalls);
  $("#missed-percentage-output").textContent = `${whole.format(inputs.missedPercentage)}%`;
  $("#opportunity-percentage-output").textContent = `${whole.format(inputs.opportunityPercentage)}%`;
  $("#job-value-output").textContent = money.format(inputs.averageJobValue);
  $("#risk-total").textContent = money.format(result.potentialRevenueAtRisk);
  $("#missed-calls").textContent = whole.format(result.missedCalls);
  $("#potential-opportunities").textContent = whole.format(result.potentialOpportunities);
  $("#risk-detail").textContent = money.format(result.potentialRevenueAtRisk);
  $("#roi-risk").innerHTML = `${money.format(result.potentialRevenueAtRisk)}<em>/mo</em>`;
  $("#roi-explanation").textContent = inputs.averageJobValue > publicConfig.recurringPrice
    ? `At a ${money.format(inputs.averageJobValue)} average job value, one additional booked job could already exceed the monthly AppsOrder fee.`
    : inputs.averageJobValue > 0
      ? `At a ${money.format(inputs.averageJobValue)} average job value, about ${Math.ceil(result.jobsToCoverMonthlyFee)} additional booked jobs could exceed the monthly AppsOrder fee.`
      : "Enter your average booked-job value to compare it with the monthly AppsOrder fee.";

  if (source !== "initial") {
    if (!calculatorStarted) {
      calculatorStarted = true;
      track("calculator_started", { source });
    }
    window.clearTimeout(calculatorCompletedTimer);
    calculatorCompletedTimer = window.setTimeout(() => {
      track("calculator_completed");
      track("calculator_result", {
        monthly_calls: inputs.monthlyCalls,
        missed_percentage: inputs.missedPercentage,
        opportunity_percentage: inputs.opportunityPercentage,
        average_job_value: inputs.averageJobValue,
        estimated_revenue_at_risk: Math.round(result.potentialRevenueAtRisk),
      });
    }, 900);
  }
}

function initializeCalculator(): void {
  for (const range of document.querySelectorAll<HTMLInputElement>(".calculator-controls input[type=range]")) {
    range.addEventListener("input", () => {
      const exact = document.querySelector<HTMLInputElement>(`.number-input[data-for="${range.id}"]`);
      if (exact) exact.value = range.value;
      updateCalculator("range");
    });
  }
  for (const exact of document.querySelectorAll<HTMLInputElement>(".number-input")) {
    exact.addEventListener("input", () => {
      const range = document.getElementById(exact.dataset.for || "") as HTMLInputElement | null;
      if (range) range.value = String(Math.min(Number(range.max), Math.max(Number(range.min), Number(exact.value) || 0)));
      updateCalculator("exact_input");
    });
  }
  updateCalculator();
}

function applyOfferConfig(): void {
  for (const element of document.querySelectorAll<HTMLElement>("[data-first-price]")) element.textContent = money.format(publicConfig.firstMonthPrice);
  for (const element of document.querySelectorAll<HTMLElement>("[data-recurring-price]")) element.textContent = money.format(publicConfig.recurringPrice);
}

function hvacWidgetSession(): { firstMessage: string; systemPrompt: string; variables: Record<string, string | number> } {
  const hour = new Date().getHours();
  const sessionMode = hour >= 18 || hour < 8 ? "after-hours" : "missed-call and overflow";
  const inputs = {
    monthlyCalls: readNumber("monthly-calls"),
    missedPercentage: readNumber("missed-percentage"),
    opportunityPercentage: readNumber("opportunity-percentage"),
    averageJobValue: readNumber("job-value"),
  };
  const estimate = calculateRevenueRisk(inputs, publicConfig.recurringPrice);
  const firstMessage = `Hi, this is the AppsOrder ${sessionMode} HVAC demo. I’m an AI receptionist. Pretend you’re calling an HVAC company because your team could not answer. What’s happening with your heating or cooling system?`;
  const systemPrompt = `You are the AppsOrder HVAC Revenue Recovery website voice demonstration.

SESSION IDENTITY
- This conversation started only from https://hvac.appsorder.com through its embedded ElevenLabs widget.
- You are a clearly disclosed AI receptionist demonstrating a fictional missed, overflow, or after-hours HVAC call.
- This is not a live HVAC company, dispatch center, or emergency service.

PRIMARY TASK
- Ask the visitor to pretend they are an HVAC customer.
- Handle the call naturally and ask one short question at a time.
- Collect only the sample intake details relevant to the scenario: caller name, callback number, service address, heating or cooling problem, urgency, and preferred timing.
- Briefly summarize the captured opportunity at the end and explain that a configured real workflow could create a callback request, booking, escalation, or staff notification only where supported.

TRUTH AND SAFETY
- Clearly say this is an AppsOrder AI demo if there could be any confusion.
- Never claim a technician is available, dispatched, or on the way.
- Never promise an appointment, response time, emergency response, price, service area, diagnosis, repair outcome, integration, or revenue result.
- Do not give technical repair instructions. For immediate danger involving gas, smoke, fire, carbon monoxide, or electrical hazards, tell the caller to leave the area when appropriate and contact local emergency services or the relevant utility.
- Do not treat sample caller details as a real service request and do not use lead, checkout, SMS, email, booking, or dispatch tools during the role-play.
- If the visitor asks about buying AppsOrder, explain the current website offer accurately: $299 for the first month, then $399 per month, and direct them to the form on this page. Do not invent contract or cancellation terms.

STYLE
- Professional, calm, practical, concise, and natural for a US HVAC business.
- Do not use AI jargon. Focus on the caller’s problem and the next useful intake question.
- Allow interruptions, corrections, and changes of mind.

WEBSITE CONTEXT
- Session mode: ${sessionMode}.
- The visitor’s editable calculator currently estimates ${money.format(estimate.potentialRevenueAtRisk)} per month of opportunity value at risk from the assumptions they entered. This is an estimate, not a guaranteed result. Mention it only if the visitor asks about the business case.`;

  return {
    firstMessage,
    systemPrompt,
    variables: {
      session_mode: sessionMode,
      calculator_monthly_calls: inputs.monthlyCalls,
      calculator_missed_percentage: inputs.missedPercentage,
      calculator_opportunity_percentage: inputs.opportunityPercentage,
      calculator_average_job_value: inputs.averageJobValue,
      calculator_estimated_revenue_at_risk: Math.round(estimate.potentialRevenueAtRisk),
    },
  };
}

async function loadElevenLabsWidget(source = "live_demo_section"): Promise<void> {
  const target = $("#demo-widget");
  const button = $<HTMLButtonElement>("#demo-start");
  track("live_demo_clicked", { source });
  track("demo_cta_click", { source });
  if (target.querySelector("elevenlabs-convai")) {
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    return;
  }
  if (!publicConfig.elevenLabsAgentId) {
    target.innerHTML = '<div class="demo-placeholder"><strong>Live demo configuration needed.</strong><br />Set <code>PUBLIC_ELEVENLABS_HVAC_AGENT_ID</code> in the Cloudflare Pages environment to activate this experience.</div>';
    target.scrollIntoView({ block: "nearest", behavior: "smooth" });
    return;
  }
  button.disabled = true;
  button.textContent = "Loading live call…";
  try {
    // Define the custom element first so its worklet paths are present before
    // ElevenLabs initializes the audio session on mobile browsers.
    await ensureElevenLabsWidgetDefined();
  } catch {
    target.innerHTML = '<div class="demo-placeholder"><strong>The live call could not load.</strong><br />Please check your connection and try again.</div>';
    button.disabled = false;
    button.textContent = "Try the live HVAC AI →";
    target.scrollIntoView({ block: "nearest", behavior: "smooth" });
    return;
  }
  const widget = document.createElement("elevenlabs-convai");
  const session = hvacWidgetSession();
  widget.setAttribute("agent-id", publicConfig.elevenLabsAgentId);
  widget.setAttribute("variant", "expanded");
  widget.setAttribute("default-expanded", "true");
  widget.setAttribute("always-expanded", "true");
  widget.setAttribute("dismissible", "false");
  widget.setAttribute("action-text", "Talk to the HVAC receptionist");
  widget.setAttribute("start-call-text", "Start HVAC voice demo");
  widget.setAttribute("end-call-text", "End call");
  widget.setAttribute("expand-text", "Talk to the HVAC AI");
  widget.setAttribute("listening-text", "Listening...");
  widget.setAttribute("speaking-text", "AppsOrder speaking");
  widget.setAttribute("avatar-orb-color-1", "#f4b943");
  widget.setAttribute("avatar-orb-color-2", "#2f7f6b");
  widget.setAttribute("worklet-path-raw-audio-processor", "/elevenlabs/rawAudioProcessor.js");
  widget.setAttribute("worklet-path-audio-concat-processor", "/elevenlabs/audioConcatProcessor.js");
  widget.setAttribute("worklet-path-libsamplerate", "/elevenlabs/libsamplerate.worklet.js");
  widget.setAttribute("override-prompt", session.systemPrompt);
  widget.setAttribute("override-first-message", session.firstMessage);
  widget.setAttribute("dynamic-variables", JSON.stringify({
    business_name: "AppsOrder HVAC Revenue Recovery Demo",
    business_aliases: "AppsOrder HVAC; AppsOrder HVAC Revenue Recovery; AppsOrder",
    business_category: "HVAC",
    service_area: "United States demo — no live dispatch area",
    service_focus: "missed, overflow, busy-hour, and after-hours HVAC lead intake",
    demo_code: "",
    demo_code_for_agent: "",
    known_phone_numbers: "No customer phone system is connected in this public demo.",
    forwarding_phone: "",
    conversation_start_context: session.systemPrompt,
    demo_slug: "appsorder-hvac-revenue-recovery",
    widget_source: "hvac_revenue_recovery_site",
    widget_host: "hvac.appsorder.com",
    ...session.variables,
  }));
  widget.id = "hvac-voice-widget";
  target.replaceChildren(widget);
  target.classList.add("has-widget");
  target.closest(".demo-console")?.classList.add("widget-active");
  button.hidden = true;
  target.scrollIntoView({ block: "center", behavior: "smooth" });
  track("live_demo_started");
  track("demo_start");
  let completed = false;
  const complete = () => {
    if (completed) return;
    completed = true;
    track("demo_complete");
  };
  for (const eventName of ["conversation-end", "conversation-ended", "elevenlabs-convai:conversation-end"]) widget.addEventListener(eventName, complete, { once: true });
}

function initializeDemo(): void {
  $("#demo-start").addEventListener("click", () => void loadElevenLabsWidget("live_demo_section"));
  for (const link of document.querySelectorAll<HTMLAnchorElement>(".demo-link")) link.addEventListener("click", (event) => {
    event.preventDefault();
    $("#live-demo").scrollIntoView({ block: "start", behavior: "smooth" });
    void loadElevenLabsWidget(link.dataset.demoSource || "section_link");
  });
}

function attribution(): Record<string, string> {
  const params = new URLSearchParams(location.search);
  const get = (key: string) => params.get(key)?.slice(0, 240) || "";
  return {
    landing_page: `${location.origin}${location.pathname}`,
    referrer: document.referrer.slice(0, 500),
    utm_source: get("utm_source"),
    utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_content: get("utm_content"),
    utm_term: get("utm_term"),
    timestamp: new Date().toISOString(),
  };
}

function validateForm(form: HTMLFormElement): string {
  let firstInvalid: HTMLInputElement | null = null;
  for (const input of form.querySelectorAll<HTMLInputElement>("input[required]")) {
    input.setAttribute("aria-invalid", String(!input.validity.valid));
    if (!input.validity.valid && !firstInvalid) firstInvalid = input;
  }
  if (firstInvalid) {
    firstInvalid.focus();
    return firstInvalid.validity.typeMismatch ? "Enter a valid email address." : "Complete all four required fields.";
  }
  const phone = new FormData(form).get("phone")?.toString() || "";
  if (phone.replace(/\D/g, "").length < 7) {
    (form.elements.namedItem("phone") as HTMLInputElement | null)?.focus();
    return "Enter a valid phone number.";
  }
  return "";
}

function initializeLeadForm(): void {
  const form = $<HTMLFormElement>("#lead-capture");
  const startedAt = form.elements.namedItem("form_started_at") as HTMLInputElement;
  startedAt.value = String(Date.now());
  let started = false;
  let submitting = false;
  form.addEventListener("focusin", () => {
    if (!started) {
      started = true;
      track("lead_form_started");
    }
  });
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (submitting) return;
    const error = $("#form-error") as HTMLElement;
    const validationMessage = validateForm(form);
    if (validationMessage) {
      error.textContent = validationMessage;
      error.hidden = false;
      return;
    }
    error.hidden = true;
    submitting = true;
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    submit.disabled = true;
    submit.textContent = "Submitting…";
    const values = Object.fromEntries(new FormData(form).entries());
    const payload = { ...values, ...attribution(), idempotency_key: crypto.randomUUID() };
    try {
      const response = await fetch("/api/lead", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(payload) });
      const result = await response.json() as { ok?: boolean; error?: string };
      if (!response.ok || !result.ok) throw new Error(result.error || "We could not submit your information.");
      track("lead_submitted", { has_website: Boolean(values.website), calls_month: Number(values.approximate_calls_month) || 0 });
      form.innerHTML = '<div class="success-card" role="status"><span>✓</span><h3>We received your information.</h3><p>AppsOrder will review your HVAC call setup and follow up using the contact details you provided.</p></div>';
    } catch (cause) {
      track("lead_submission_failed", { reason: cause instanceof Error ? cause.message.slice(0, 120) : "unknown" });
      error.textContent = cause instanceof Error ? cause.message : "We could not submit your information. Please try again.";
      error.hidden = false;
      submit.disabled = false;
      submit.textContent = "Start my first month →";
      submitting = false;
    }
  });
}

function initializeViewEvents(): void {
  const pricing = document.getElementById("pricing");
  if (!pricing) return;
  let seen = false;
  const observer = new IntersectionObserver((entries) => {
    if (!seen && entries.some((entry) => entry.isIntersecting)) {
      seen = true;
      track("pricing_viewed");
      observer.disconnect();
    }
  }, { threshold: 0.35 });
  observer.observe(pricing);
}

async function initialize(): Promise<void> {
  publicConfig = await loadPublicConfig();
  applyOfferConfig();
  initializeCalculator();
  initializeDemo();
  initializeLeadForm();
  initializeViewEvents();
  $("#year").textContent = String(new Date().getFullYear());
  track("page_view", attribution());
}

void initialize();
